import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './SplashScreen.css';

/**
 * SimpleSplash - A streamlined first-launch animation component
 *
 * @param {Object} props
 * @param {string} props.blueprintName - Name of the blueprint to display
 * @param {boolean} props.forceShow - Force showing splash even if seen before (for testing)
 * @param {function} props.onComplete - Optional callback when animation completes
 * @param {string} props.businessColor - Optional business color (defaults to CSS variable)
 */
const SplashScreen = ({
  blueprintName = 'BlueLink',
  forceShow = false,
  onComplete,
  businessColor,
}) => {
  const { t } = useTranslation();
  const [showSplash, setShowSplash] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);
  const [zoomIn, setZoomIn] = useState(false);
  const location = useLocation();
  const defaultBlue = '#007AFF';
  const animationRef = useRef(null);
  const [targetColor, setTargetColor] = useState(defaultBlue);
  const [styles, setStyles] = useState({
    circle: {},
    button: {},
    accent: {},
  });

  // Get the blueprint ID from URL
  const blueprintId = location.pathname.split('/').pop();
  const storageKey = `bluelinkSplashSeen_${blueprintId}`;

  // Set color variables and prepare styles
  useEffect(() => {
    // Get business color from props or CSS variable with proper validation
    let color = businessColor;

    if (!color) {
      try {
        // Try to get business color from CSS variable
        color = getComputedStyle(document.documentElement)
          .getPropertyValue('--businessColor')
          .trim();
      } catch (e) {}
    }

    // Ensure color has # prefix if it's a valid hex without it
    if (color && !color.startsWith('#') && /^[0-9A-Fa-f]{6}$/.test(color)) {
      color = `#${color}`;
    }

    // Use default if no business color found or invalid
    if (!color || color === '') {
      color = defaultBlue;
    }

    // Store the color in state
    setTargetColor(color);

    // Set splash color variables
    document.documentElement.style.setProperty('--splash-blue', defaultBlue);
    document.documentElement.style.setProperty('--target-color', color);
    document.documentElement.style.setProperty(
      '--splash-light-blue',
      '#e6f2ff'
    );

    // Prepare inline styles
    setStyles({
      circle: {
        background: zoomIn
          ? color
          : `radial-gradient(circle at center, ${color}80 0%, ${color} 70%)`,
        boxShadow: `0 0 60px 30px ${color}20`,
      },
      button: {
        backgroundColor: color,
        boxShadow: `0 5px 15px ${color}40`,
      },
      accent: {
        backgroundColor: color,
      },
    });

    return () => {
      document.documentElement.style.removeProperty('--splash-blue');
      document.documentElement.style.removeProperty('--target-color');
      document.documentElement.style.removeProperty('--splash-light-blue');
    };
  }, [businessColor, defaultBlue, zoomIn]);

  // Apply the inner circle color through direct DOM manipulation
  useEffect(() => {
    // This is separated to ensure we only run it once the ref is available
    const applyInnerCircleStyles = () => {
      if (!animationRef.current) return;

      try {
        const className = 'inner-circle-color';

        // Try to find if there's already a style element for our dynamic styles
        let styleEl = document.getElementById('splash-dynamic-styles');

        if (!styleEl) {
          // Create it if it doesn't exist
          styleEl = document.createElement('style');
          styleEl.id = 'splash-dynamic-styles';
          document.head.appendChild(styleEl);
        }

        // Set the styles directly in the style element
        styleEl.textContent = `
          .${className}::after {
            background-color: ${targetColor} !important;
            box-shadow: 0 0 30px 15px ${targetColor}30 !important;
          }
        `;

        // Add class to the element
        animationRef.current.classList.add(className);
      } catch (error) {
        console.error('Error applying inner circle styles:', error);
      }
    };

    applyInnerCircleStyles();

    return () => {
      const styleEl = document.getElementById('splash-dynamic-styles');
      if (styleEl) {
        styleEl.textContent = '';
      }
    };
  }, [targetColor]);

  // Determine if splash should be shown
  useEffect(() => {
    if (forceShow) {
      setShowSplash(true);
      return;
    }

    // Check if user has seen this blueprint's splash
    const hasSeenSplash = localStorage.getItem(storageKey) === 'true';
    setShowSplash(!hasSeenSplash);
  }, [blueprintId, forceShow, storageKey]);

  // Handle continue button click
  const handleContinueClick = () => {
    // First zoom in the circle
    setZoomIn(true);

    // Get the animation element and force a repaint to ensure smooth animation
    if (animationRef.current) {
      const element = animationRef.current;
      const _ = element.offsetHeight; // Force repaint
    }

    // Then start the fade-out after an appropriate delay
    setTimeout(() => {
      setFadeOut(true);
    }, 1800);

    // Mark as seen
    localStorage.setItem(storageKey, 'true');
    localStorage.setItem('bluelinkSplashSeen', 'true');

    // Wait for animations to complete then hide
    setTimeout(() => {
      setShowSplash(false);
      if (onComplete) onComplete();
    }, 2700);
  };

  // Don't render anything if splash shouldn't be shown
  if (!showSplash) {
    return null;
  }

  return (
    <div
      className={`simple-splash ${fadeOut ? 'fade-out' : ''} ${
        zoomIn ? 'zoom-in' : ''
      }`}
    >
      {/* Background elements */}
      <div className="grid-pattern"></div>
      <div className="accent accent-1" style={styles.accent}></div>
      <div className="accent accent-2" style={styles.accent}></div>

      {/* Floating particles */}
      <div className="particles">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className={`particle particle-${i + 1}`}
            style={styles.accent}
          ></div>
        ))}
      </div>

      {/* Main content */}
      <div className={`splash-content ${zoomIn ? 'zoom-out' : ''}`}>
        <div className="splash-logo">
          <div
            ref={animationRef}
            className={`splash-circle ${zoomIn ? 'zoom-in' : ''}`}
            style={styles.circle}
          >
            <div className="fluid-overlay"></div>
          </div>
        </div>

        <div className={`splash-text ${zoomIn ? 'fade-out-fast' : ''}`}>
          {'BlueLink™'}
        </div>

        <div className={`splash-description ${zoomIn ? 'fade-out-fast' : ''}`}>
          {blueprintName + ', ' + t('splash.intro')}
        </div>

        <button
          className={`continue-button ${zoomIn ? 'fade-out-fast' : ''}`}
          onClick={handleContinueClick}
          style={styles.button}
        >
          {t('splash.continue')}
        </button>
      </div>
    </div>
  );
};

export default SplashScreen;
